import { useConvex } from 'convex/react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Fetches a user's current profile image from Clerk (via a Convex action) and
 * caches it client-side for 5 minutes. The result is shared across all callers
 * with the same clerkId, so the same image is fetched at most once per stale window.
 *
 * Returns `undefined` while loading, `null` when no image is available or the
 * fetch fails (callers should fall back to their own placeholder), or the image URL.
 */
export const useClerkUserImage = (clerkId: string | undefined): string | null | undefined => {
  const convex = useConvex();
  const { data } = useQuery({
    queryKey: ['clerk-user-image', clerkId],
    queryFn: async (): Promise<string | null> => {
      if (!clerkId) return null;

      try {
        return (await convex.action(api.app.users.actions.getClerkUserImage, { clerkId })) as string | null;
      } catch (error) {
        console.warn('[useClerkUserImage] Failed to fetch image from Clerk:', error);
        return null;
      }
    },
    enabled: Boolean(clerkId),
    staleTime: 5 * 60 * 1000,
  });

  return data;
};
